
import React, { useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from 'axios';
import { MapPin } from 'lucide-react';

const locations = [
    {
        city: "Chicago",
        state: "IL",
        address: "The Loop, Chicago, IL 60601",
        image: "https://images.unsplash.com/photo-1631548637245-043803a8b776?q=80&w=2070&auto=format&fit=crop"
    },
    {
        city: "Toronto",
        state: "ON",
        address: "Financial District, Toronto, ON M5H",
        image: "https://images.unsplash.com/photo-1559869824-929df9dab35e?q=80&w=2070&auto=format&fit=crop"
    },
    {
        city: "Mumbai",
        state: "MH",
        address: "Bandra Kurla Complex, Mumbai, MH 400051",
        image: "https://images.unsplash.com/photo-1666843527155-14ec5f016802?q=80&w=2070&auto=format&fit=crop"
    },
    {
        city: "Bangalore",
        state: "KA",
        address: "Whitefield, Bangalore, KA 560066",
        image: "https://images.unsplash.com/photo-1687158266872-fd2773fa76c6?q=80&w=2070&auto=format&fit=crop"
    }
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    reason: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [activeLocation, setActiveLocation] = useState(0);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
          name: formData.name,
          email: formData.email,
          company: formData.company,
          message: `Reason: ${formData.reason}\nPhone: ${formData.phone}\n\n${formData.message}`
      };

      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, payload);
      
      toast.success("Email sent to hello@vedara.ai successfully!");
      setFormData({ name: '', email: '', phone: '', company: '', reason: '', message: '' });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      <Navbar />
      
      {/* Hero / Form Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
         <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
             <div>
                 <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 leading-tight">
                    Let’s Transform Your <span className="text-vedara-blue-400">Industry</span>
                 </h1>
                 <p className="text-xl text-gray-400 max-w-lg mt-6 leading-relaxed">
                    Ready to deploy autonomous agents that solve your hardest problems? Get in touch with our forward-deployed team.
                 </p>
             </div>
             
             <div className="bg-[#0f172a] p-8 rounded-2xl shadow-xl border border-gray-800">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <Input 
                        name="reason" 
                        placeholder="Reason For Contacting Us?" 
                        value={formData.reason} 
                        onChange={handleChange}
                        className="bg-background border-gray-700 h-12 text-white placeholder:text-gray-500 focus:border-vedara-blue-500"
                        required
                    />
                    <Input 
                        name="name" 
                        placeholder="Name" 
                        value={formData.name} 
                        onChange={handleChange}
                        className="bg-background border-gray-700 h-12 text-white placeholder:text-gray-500 focus:border-vedara-blue-500"
                        required
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input 
                            name="email" 
                            type="email" 
                            placeholder="Email Address" 
                            value={formData.email} 
                            onChange={handleChange}
                            className="bg-background border-gray-700 h-12 text-white placeholder:text-gray-500 focus:border-vedara-blue-500"
                            required
                        />
                        <Input 
                            name="phone" 
                            type="tel" 
                            placeholder="Phone Number" 
                            value={formData.phone} 
                            onChange={handleChange}
                            className="bg-background border-gray-700 h-12 text-white placeholder:text-gray-500 focus:border-vedara-blue-500"
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input 
                            name="company" 
                            placeholder="Company" 
                            value={formData.company} 
                            onChange={handleChange}
                            className="bg-background border-gray-700 h-12 text-white placeholder:text-gray-500 focus:border-vedara-blue-500"
                            required
                        />
                         <Select onValueChange={(val) => setFormData({...formData, company: formData.company})}> 
                            <SelectTrigger className="bg-background border-gray-700 h-12 text-gray-400">
                                <SelectValue placeholder="Existing Customer?" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0f172a] border-gray-800 text-white">
                                <SelectItem value="yes">Yes</SelectItem>
                                <SelectItem value="no">No</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Textarea 
                        name="message" 
                        placeholder="Message" 
                        value={formData.message} 
                        onChange={handleChange}
                        className="bg-background border-gray-700 min-h-[150px] text-white placeholder:text-gray-500 focus:border-vedara-blue-500"
                        required
                    />
                    <Button 
                        type="submit" 
                        className="w-full bg-vedara-blue-600 hover:bg-vedara-blue-500 text-white h-12 text-lg font-bold"
                        disabled={loading}
                    >
                        {loading ? "Sending..." : "Submit"}
                    </Button>
                </form>
             </div>
         </div>
      </section>

      {/* Locations Section */}
      <section className="relative h-[600px] overflow-hidden transition-all duration-700 ease-in-out">
          {/* Background Images Layer */}
          {locations.map((loc, index) => (
              <div 
                key={index}
                className={`absolute inset-0 bg-cover bg-center transition-opacity duration-700 ease-in-out ${activeLocation === index ? 'opacity-100' : 'opacity-0'}`}
                style={{ backgroundImage: `url(${loc.image})` }}
              >
                  <div className="absolute inset-0 bg-background/60" /> {/* Darker Overlay for Text Contrast */}
              </div>
          ))}

          <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
              <h2 className="text-white text-4xl font-bold mb-12">
                  Vedara has <span className="text-gray-400">offices in</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {locations.map((loc, index) => (
                      <div 
                        key={index} 
                        className={`p-6 border-l-2 cursor-pointer transition-all duration-300 ${activeLocation === index ? 'border-vedara-blue-400 bg-background/80 backdrop-blur-md shadow-lg' : 'border-white/10 hover:bg-white/5'}`}
                        onMouseEnter={() => setActiveLocation(index)}
                      >
                          <h3 className={`text-xl font-bold mb-2 ${activeLocation === index ? 'text-white' : 'text-gray-400'}`}>
                              {loc.city}, {loc.state}
                          </h3>
                          <p className="text-gray-500 text-sm mb-4 h-10">{loc.address}</p>
                          <div className={`inline-flex items-center text-sm font-semibold ${activeLocation === index ? 'text-vedara-blue-400' : 'text-gray-500'}`}>
                              <MapPin size={14} className="mr-1"/> Office
                          </div>
                      </div>
                  ))}
              </div>
          </div>
      </section>

      {/* Get in Touch Grid */}
      <section className="py-20 bg-background border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-white mb-12 border-b border-gray-800 pb-6">Get in touch</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                  <div>
                      <p className="text-xl text-gray-400 leading-relaxed">
                          Reach out to us at <a href="mailto:hello@vedara.ai" className="text-vedara-blue-400 font-semibold hover:underline">hello@vedara.ai</a> to schedule a call and start building.
                      </p>
                  </div>
                   <div>
                      <p className="text-xl text-gray-400 leading-relaxed">
                          For press or media inquiries,<br/>
                          reach out at to us at <a href="mailto:media@vedara.ai" className="text-vedara-blue-400 font-semibold hover:underline">media@vedara.ai</a>
                      </p>
                  </div>
                   <div>
                      <p className="text-xl text-gray-400 leading-relaxed mb-6">
                          Interested in joining the team?<br/>
                          Visit our careers page or email <a href="mailto:careers@vedara.ai" className="text-vedara-blue-400 font-semibold hover:underline">careers@vedara.ai</a>
                      </p>
                      <Button variant="outline" className="rounded-full border-gray-700 text-gray-300 hover:bg-vedara-blue-600 hover:text-white hover:border-vedara-blue-600" onClick={() => window.location.href='/careers'}>
                          See Open Positions
                      </Button>
                  </div>
              </div>
          </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
